import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiSlash,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    cancelDebitNote,
    confirmDebitNote,
    fetchDebitNoteById,
    selectedDebitNoteSelector,
    debitNoteLoadingSelector,
    debitNoteErrorSelector,
} from "../../../../../../../store/billing/debitNotes/debitNoteSlice";

import DebitNoteItemsTable from "./DebitNoteItemsTable";
import DebitNoteSummary from "./DebitNoteSummary";

import "../DebitNotes.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );


const formatDate = (
    value,
    includeTime = false
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",

                  hour:
                      "2-digit",

                  minute:
                      "2-digit",
              }
            : {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",
              }
    ).format(
        date
    );
};


const formatLabel = (
    value
) =>
    String(
        value ||
        "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );


const getDebitAmount = (
    debitNote
) =>
    roundMoney(
        debitNote
            ?.totals
            ?.debitAmount ??
        debitNote
            ?.debitAmount ??
        0
    );


const getSupplierName = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.supplierName ||
    debitNote
        ?.supplierSnapshot
        ?.vendorName ||
    debitNote
        ?.supplier
        ?.supplierName ||
    debitNote
        ?.supplier
        ?.name ||
    "Supplier";


const getSupplierMobile = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.mobile ||
    debitNote
        ?.supplier
        ?.mobile ||
    "-";


const getSupplierEmail = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.email ||
    debitNote
        ?.supplier
        ?.email ||
    "-";


const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload
        ?.message ||
    error?.payload ||
    error?.response
        ?.data
        ?.message ||
    fallback;


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const DebitNoteDetailsDialog = ({
    open,
    debitNote,
    onClose,
    onUpdated,
}) => {
    const dispatch =
        useDispatch();

    const selectedDebitNote =
        useSelector(
            selectedDebitNoteSelector
        );

    const loading =
        useSelector(
            debitNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            debitNoteErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | LOCAL STATE
    |--------------------------------------------------------------------------
    */

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        actionLoading,
        setActionLoading,
    ] = useState("");

    const [
        cancelDialogOpen,
        setCancelDialogOpen,
    ] = useState(
        false
    );

    const [
        cancellationReason,
        setCancellationReason,
    ] = useState(
        ""
    );


    /*
    |--------------------------------------------------------------------------
    | DEBIT NOTE ID
    |--------------------------------------------------------------------------
    */

    const debitNoteId =
        debitNote?._id ||
        selectedDebitNote?._id ||
        "";


    /*
    |--------------------------------------------------------------------------
    | LOAD FULL DETAILS
    |--------------------------------------------------------------------------
    */

    const loadDetails =
        useCallback(
            async () => {
                if (
                    !open ||
                    !debitNoteId
                ) {
                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        fetchDebitNoteById(
                            debitNoteId
                        )
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to load debit note details."
                        )
                    );
                }
            },
            [
                dispatch,
                open,
                debitNoteId,
            ]
        );


    useEffect(
        () => {
            loadDetails();
        },
        [
            loadDetails,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | RESET CANCEL DIALOG
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open
            ) {
                setCancelDialogOpen(
                    false
                );

                setCancellationReason(
                    ""
                );

                setLocalError(
                    ""
                );

                setActionLoading(
                    ""
                );
            }
        },
        [
            open,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | DISPLAY RECORD
    |--------------------------------------------------------------------------
    */

    const record =
        selectedDebitNote?._id ===
        debitNoteId
            ? selectedDebitNote
            : debitNote;


    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const status =
        String(
            record?.status ||
            "DRAFT"
        ).toUpperCase();


    /*
    |--------------------------------------------------------------------------
    | CAN CANCEL
    |--------------------------------------------------------------------------
    |
    | DRAFT:
    |     simply cancel
    |
    | CONFIRMED:
    |     backend will:
    |       - restore inventory
    |       - reverse supplier ledger
    |       - restore purchase bill
    |--------------------------------------------------------------------------
    */

    const canCancelDebitNote =
        [
            "DRAFT",
            "CONFIRMED",
        ].includes(
            status
        );


    /*
    |--------------------------------------------------------------------------
    | ITEMS
    |--------------------------------------------------------------------------
    */

    const items =
        useMemo(
            () =>
                Array.isArray(
                    record?.items
                )
                    ? record.items
                    : [],
            [
                record,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async () => {
            if (
                !debitNoteId ||
                status !==
                    "DRAFT"
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm debit note ${
                        record
                            ?.debitNoteNumber ||
                        ""
                    }?\n\nInventory will be reduced and supplier payable will be adjusted.`
                );

            if (
                !approved
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CONFIRM"
                );

                await dispatch(
                    confirmDebitNote(
                        debitNoteId
                    )
                ).unwrap();

                await loadDetails();

                await onUpdated?.();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm debit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };


    /*
    |--------------------------------------------------------------------------
    | OPEN CANCEL DIALOG
    |--------------------------------------------------------------------------
    */

    const handleOpenCancel =
        () => {
            if (
                !debitNoteId ||
                !canCancelDebitNote
            ) {
                return;
            }

            setLocalError(
                ""
            );

            setCancellationReason(
                ""
            );

            setCancelDialogOpen(
                true
            );
        };


    /*
    |--------------------------------------------------------------------------
    | CLOSE CANCEL DIALOG
    |--------------------------------------------------------------------------
    */

    const handleCloseCancel =
        () => {
            if (
                actionLoading ===
                "CANCEL"
            ) {
                return;
            }

            setCancelDialogOpen(
                false
            );

            setCancellationReason(
                ""
            );
        };


    /*
    |--------------------------------------------------------------------------
    | CANCEL DEBIT NOTE
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async () => {
            if (
                !debitNoteId ||
                !canCancelDebitNote
            ) {
                return;
            }

            const normalizedReason =
                String(
                    cancellationReason ||
                    ""
                ).trim();

            if (
                normalizedReason.length <
                3
            ) {
                setLocalError(
                    "Cancellation reason must contain at least 3 characters."
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CANCEL"
                );

                await dispatch(
                    cancelDebitNote({
                        debitNoteId,

                        cancellationReason:
                            normalizedReason,
                    })
                ).unwrap();


                /*
                |--------------------------------------------------------------------------
                | CLOSE CANCEL DIALOG
                |--------------------------------------------------------------------------
                */

                setCancelDialogOpen(
                    false
                );

                setCancellationReason(
                    ""
                );


                /*
                |--------------------------------------------------------------------------
                | RELOAD DEBIT NOTE
                |--------------------------------------------------------------------------
                */

                await loadDetails();


                /*
                |--------------------------------------------------------------------------
                | REFRESH PARENT LIST
                |--------------------------------------------------------------------------
                */

                await onUpdated?.();

            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel debit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };


    /*
    |--------------------------------------------------------------------------
    | PRINT
    |--------------------------------------------------------------------------
    */

    const handlePrint =
        () => {
            window.print();
        };


    /*
    |--------------------------------------------------------------------------
    | CLOSED
    |--------------------------------------------------------------------------
    */

    if (
        !open
    ) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <>
            <div className="debit-note-details-overlay">
                <div className="debit-note-details-dialog">

                    {/* HEADER */}

                    <header className="debit-note-details-header">
                        <div>
                            <small>
                                DEBIT NOTE
                            </small>

                            <h2>
                                {record
                                    ?.debitNoteNumber ||
                                    "Debit Note Details"}
                            </h2>

                            <p>
                                Against purchase bill{" "}
                                {record
                                    ?.purchaseBillNumber ||
                                    "-"}
                            </p>
                        </div>

                        <button
                            type="button"
                            className="debit-note-button"
                            disabled={
                                Boolean(
                                    actionLoading
                                )
                            }
                            onClick={
                                onClose
                            }
                        >
                            <FiX />
                        </button>
                    </header>


                    {/* BODY */}

                    <div className="debit-note-details-body">

                        {(localError ||
                            errors.details ||
                            errors.confirm ||
                            errors.cancel) && (
                            <div className="debit-note-alert">
                                {localError ||
                                    errors.details ||
                                    errors.confirm ||
                                    errors.cancel}
                            </div>
                        )}


                        {loading.details &&
                        !record ? (
                            <div className="debit-note-empty">
                                <FiLoader className="debit-note-spinner" />

                                <h3>
                                    Loading debit note...
                                </h3>
                            </div>
                        ) : (
                            <>

                                {/* TOP SUMMARY */}

                                <div className="debit-note-summary-grid">

                                    <div className="debit-note-summary-card">
                                        <span>
                                            Status
                                        </span>

                                        <strong>
                                            <span
                                                className={`debit-note-status debit-note-status-${status.toLowerCase()}`}
                                            >
                                                {status ===
                                                "CONFIRMED" ? (
                                                    <FiCheckCircle />
                                                ) : status ===
                                                  "CANCELLED" ? (
                                                    <FiSlash />
                                                ) : (
                                                    <FiClock />
                                                )}

                                                {formatLabel(
                                                    status
                                                )}
                                            </span>
                                        </strong>

                                        <small>
                                            Document status
                                        </small>
                                    </div>


                                    <div className="debit-note-summary-card">
                                        <span>
                                            Supplier
                                        </span>

                                        <strong>
                                            {getSupplierName(
                                                record
                                            )}
                                        </strong>

                                        <small>
                                            {getSupplierMobile(
                                                record
                                            )}
                                        </small>
                                    </div>


                                    <div className="debit-note-summary-card">
                                        <span>
                                            Purchase Bill
                                        </span>

                                        <strong>
                                            {record
                                                ?.purchaseBillNumber ||
                                                "-"}
                                        </strong>

                                        <small>
                                            {formatDate(
                                                record
                                                    ?.purchaseBillDate
                                            )}
                                        </small>
                                    </div>


                                    <div className="debit-note-summary-card debit-note-summary-card-amount">
                                        <span>
                                            Debit Amount
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                getDebitAmount(
                                                    record
                                                )
                                            )}
                                        </strong>

                                        <small>
                                            {formatDate(
                                                record
                                                    ?.debitNoteDate
                                            )}
                                        </small>
                                    </div>

                                </div>


                                {/* DOCUMENT INFORMATION */}

                                <section className="debit-note-view-card">
                                    <div className="debit-note-view-info-grid">

                                        <div>
                                            <span>
                                                Debit Note Number
                                            </span>

                                            <strong>
                                                {record
                                                    ?.debitNoteNumber ||
                                                    "-"}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Debit Note Date
                                            </span>

                                            <strong>
                                                {formatDate(
                                                    record
                                                        ?.debitNoteDate
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Financial Year
                                            </span>

                                            <strong>
                                                {record
                                                    ?.financialYear ||
                                                    "-"}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Reason
                                            </span>

                                            <strong>
                                                {formatLabel(
                                                    record
                                                        ?.reason
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Supplier Mobile
                                            </span>

                                            <strong>
                                                {getSupplierMobile(
                                                    record
                                                )}
                                            </strong>
                                        </div>


                                        <div>
                                            <span>
                                                Supplier Email
                                            </span>

                                            <strong>
                                                {getSupplierEmail(
                                                    record
                                                )}
                                            </strong>
                                        </div>

                                    </div>


                                    {record
                                        ?.reasonDetails && (
                                        <div
                                            style={{
                                                marginTop:
                                                    "12px",
                                            }}
                                        >
                                            <strong
                                                style={{
                                                    display:
                                                        "block",

                                                    marginBottom:
                                                        "4px",

                                                    fontSize:
                                                        "10px",
                                                }}
                                            >
                                                Reason Details
                                            </strong>

                                            <div
                                                style={{
                                                    fontSize:
                                                        "11px",

                                                    color:
                                                        "#475569",
                                                }}
                                            >
                                                {
                                                    record
                                                        .reasonDetails
                                                }
                                            </div>
                                        </div>
                                    )}

                                </section>


                                {/* ITEMS */}

                                <section className="debit-note-view-card">

                                    <div
                                        style={{
                                            display:
                                                "flex",

                                            justifyContent:
                                                "space-between",

                                            alignItems:
                                                "center",

                                            marginBottom:
                                                "12px",
                                        }}
                                    >
                                        <div>
                                            <h3
                                                style={{
                                                    margin:
                                                        0,

                                                    fontSize:
                                                        "14px",
                                                }}
                                            >
                                                Returned Items
                                            </h3>

                                            <p
                                                style={{
                                                    margin:
                                                        "3px 0 0",

                                                    color:
                                                        "#64748b",

                                                    fontSize:
                                                        "10px",
                                                }}
                                            >
                                                Items returned to the supplier.
                                            </p>
                                        </div>

                                        <span>
                                            {items.length}{" "}
                                            {items.length ===
                                            1
                                                ? "item"
                                                : "items"}
                                        </span>
                                    </div>


                                    <DebitNoteItemsTable
                                        items={
                                            items
                                        }
                                    />

                                </section>


                                {/* SUMMARY */}

                                <DebitNoteSummary
                                    debitNote={
                                        record
                                    }
                                />


                                {/* NOTES */}

                                {(record?.notes ||
                                    record?.internalNotes) && (
                                    <section className="debit-note-view-card">
                                        <h3
                                            style={{
                                                margin:
                                                    "0 0 10px",

                                                fontSize:
                                                    "14px",
                                            }}
                                        >
                                            Notes
                                        </h3>

                                        {record
                                            ?.notes && (
                                            <p
                                                style={{
                                                    margin:
                                                        "0 0 8px",

                                                    fontSize:
                                                        "11px",

                                                    color:
                                                        "#475569",
                                                }}
                                            >
                                                {
                                                    record
                                                        .notes
                                                }
                                            </p>
                                        )}

                                        {record
                                            ?.internalNotes && (
                                            <p
                                                style={{
                                                    margin:
                                                        0,

                                                    fontSize:
                                                        "10px",

                                                    color:
                                                        "#64748b",
                                                }}
                                            >
                                                Internal:{" "}
                                                {
                                                    record
                                                        .internalNotes
                                                }
                                            </p>
                                        )}
                                    </section>
                                )}


                                {/* CANCELLATION DETAILS */}

                                {status ===
                                    "CANCELLED" && (
                                    <section className="debit-note-view-card">
                                        <div className="debit-note-alert">
                                            <div>
                                                <strong>
                                                    Cancelled
                                                </strong>

                                                <div
                                                    style={{
                                                        marginTop:
                                                            "3px",
                                                    }}
                                                >
                                                    {record
                                                        ?.cancellationReason ||
                                                        "-"}
                                                </div>

                                                <small>
                                                    {formatDate(
                                                        record
                                                            ?.cancelledAt,
                                                        true
                                                    )}
                                                </small>
                                            </div>
                                        </div>
                                    </section>
                                )}

                            </>
                        )}

                    </div>


                    {/* FOOTER */}

                    <footer className="debit-note-details-footer">

                        <button
                            type="button"
                            className="debit-note-button"
                            onClick={
                                handlePrint
                            }
                        >
                            <FiPrinter />

                            Print
                        </button>


                        {/* CANCEL DRAFT OR CONFIRMED */}

                        {canCancelDebitNote && (
                            <button
                                type="button"
                                className="debit-note-button debit-note-button-danger"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleOpenCancel
                                }
                            >
                                {actionLoading ===
                                "CANCEL" ? (
                                    <FiLoader className="debit-note-spinner" />
                                ) : (
                                    <FiSlash />
                                )}

                                {status ===
                                "DRAFT"
                                    ? "Cancel Draft"
                                    : "Cancel Debit Note"}
                            </button>
                        )}


                        {/* CONFIRM ONLY DRAFT */}

                        {status ===
                            "DRAFT" && (
                            <button
                                type="button"
                                className="debit-note-button debit-note-button-success"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleConfirm
                                }
                            >
                                {actionLoading ===
                                "CONFIRM" ? (
                                    <FiLoader className="debit-note-spinner" />
                                ) : (
                                    <FiCheckCircle />
                                )}

                                Confirm
                            </button>
                        )}


                        <button
                            type="button"
                            className="debit-note-button"
                            disabled={
                                Boolean(
                                    actionLoading
                                )
                            }
                            onClick={
                                onClose
                            }
                        >
                            Close
                        </button>

                    </footer>

                </div>
            </div>


            {/* CANCEL DIALOG */}

            {cancelDialogOpen && (
                <div className="debit-note-cancel-overlay">

                    <div className="debit-note-cancel-dialog">

                        {/* HEADER */}

                        <div className="debit-note-cancel-header">
                            <div>
                                <small>
                                    DEBIT NOTE
                                </small>

                                <h3>
                                    {status ===
                                    "CONFIRMED"
                                        ? "Cancel Confirmed Debit Note"
                                        : "Cancel Draft Debit Note"}
                                </h3>

                                <p>
                                    {record
                                        ?.debitNoteNumber ||
                                        "Debit Note"}
                                </p>
                            </div>

                            <button
                                type="button"
                                className="debit-note-button"
                                disabled={
                                    actionLoading ===
                                    "CANCEL"
                                }
                                onClick={
                                    handleCloseCancel
                                }
                            >
                                <FiX />
                            </button>
                        </div>


                        {/* BODY */}

                        <div className="debit-note-cancel-body">

                            {status ===
                                "CONFIRMED" && (
                                <div className="debit-note-cancel-warning">
                                    <strong>
                                        Important
                                    </strong>

                                    <p>
                                        Cancelling this confirmed Debit Note will restore inventory,
                                        reverse the supplier ledger entry and restore the Purchase
                                        Bill payable amount.
                                    </p>
                                </div>
                            )}


                            {status ===
                                "DRAFT" && (
                                <div className="debit-note-cancel-warning">
                                    This Debit Note is still a draft and has not affected
                                    inventory or supplier ledger.
                                </div>
                            )}


                            <label>
                                Cancellation Reason *
                            </label>

                            <textarea
                                rows="4"
                                value={
                                    cancellationReason
                                }
                                placeholder="Enter cancellation reason..."
                                disabled={
                                    actionLoading ===
                                    "CANCEL"
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCancellationReason(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />


                            {localError && (
                                <div
                                    className="debit-note-alert"
                                    style={{
                                        marginTop:
                                            "10px",
                                    }}
                                >
                                    {localError}
                                </div>
                            )}

                        </div>


                        {/* FOOTER */}

                        <div className="debit-note-cancel-footer">

                            <button
                                type="button"
                                className="debit-note-button"
                                disabled={
                                    actionLoading ===
                                    "CANCEL"
                                }
                                onClick={
                                    handleCloseCancel
                                }
                            >
                                Close
                            </button>


                            <button
                                type="button"
                                className="debit-note-button debit-note-button-danger"
                                disabled={
                                    actionLoading ===
                                        "CANCEL" ||
                                    cancellationReason
                                        .trim()
                                        .length <
                                        3
                                }
                                onClick={
                                    handleCancel
                                }
                            >
                                {actionLoading ===
                                "CANCEL" ? (
                                    <>
                                        <FiLoader className="debit-note-spinner" />

                                        Cancelling...
                                    </>
                                ) : (
                                    <>
                                        <FiSlash />

                                        Confirm Cancellation
                                    </>
                                )}
                            </button>

                        </div>

                    </div>

                </div>
            )}

        </>
    );
};


export default DebitNoteDetailsDialog;